import React, { useState, useEffect } from 'react';
import { getUserPayments } from '../services/paymentService';
import { getUserInvoices, downloadInvoice, viewInvoice } from '../services/invoiceService';
import { formatAmount } from '../services/cashfreeService';
import LoadingSpinner from '../components/LoadingSpinner';

const BillingHistory = ({ user, userData }) => {
    const [payments, setPayments] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('invoices'); // 'invoices' or 'payments'

    useEffect(() => {
        if (user?.uid) {
            fetchData();
        }
    }, [user]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch payments and invoices in parallel
            const [paymentsResult, invoicesResult] = await Promise.all([
                getUserPayments(user.uid),
                getUserInvoices(user.uid)
            ]);

            let fetchedPayments = [];
            let fetchedInvoices = [];

            if (paymentsResult.success) {
                fetchedPayments = paymentsResult.payments;
                setPayments(fetchedPayments);
            }

            if (invoicesResult.success) {
                fetchedInvoices = invoicesResult.invoices;
            }

            // Generate virtual invoices for successful payments that don't have an invoice record
            // (This handles legacy data or failed invoice creation scenarios)
            const existingInvoicePaymentIds = new Set(fetchedInvoices.map(inv => inv.paymentId));

            const virtualInvoices = fetchedPayments
                .filter(p => p.status === 'success' && !existingInvoicePaymentIds.has(p.id))
                .map(p => {
                    // Generate a random invoice number if missing
                    const dateStr = p.createdAt?.toDate ?
                        p.createdAt.toDate().toISOString().slice(0, 10).replace(/-/g, '') :
                        new Date().toISOString().slice(0, 10).replace(/-/g, '');

                    return {
                        id: `virtual_${p.id}`,
                        invoiceNumber: `INV-${dateStr}-${p.id.slice(0, 4).toUpperCase()}`,
                        invoiceDate: p.createdAt,
                        paymentDate: p.createdAt,
                        paymentId: p.id,

                        // Payment IDs
                        razorpayPaymentId: p.razorpayPaymentId || null,
                        cashfreePaymentId: p.cashfreePaymentId || null,

                        // Details
                        packageName: p.packageName || 'Package',
                        packageDuration: p.duration || 30, // Default if missing

                        // Customer
                        customerName: p.userName || user.displayName || 'Customer',
                        customerEmail: p.userEmail || user.email,
                        customerPhone: p.userPhone,

                        // Amounts
                        baseAmount: p.amount,
                        gstRate: 18,
                        gstAmount: p.gstAmount || (p.totalAmount - p.amount),
                        totalAmount: p.totalAmount || p.amount,
                        currency: p.currency || 'INR',

                        isVirtual: true // Flag to indicate generated invoice
                    };
                });

            // Combine real and virtual invoices
            const allInvoices = [...fetchedInvoices, ...virtualInvoices].sort((a, b) => {
                const dateA = a.invoiceDate?.toDate ? a.invoiceDate.toDate() : new Date(a.invoiceDate);
                const dateB = b.invoiceDate?.toDate ? b.invoiceDate.toDate() : new Date(b.invoiceDate);
                return dateB - dateA; // Descending order
            });

            setInvoices(allInvoices);

        } catch (error) {
            console.error('Error fetching billing data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            success: { bg: 'bg-green-100', text: 'text-green-800', label: 'Paid' },
            pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
            failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
            refunded: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Refunded' },
        };

        const config = statusConfig[status] || statusConfig.pending;
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
                {config.label}
            </span>
        );
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Billing History</h1>
                    <p className="text-gray-600">
                        View your payment history and download invoices
                    </p>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-lg shadow-sm mb-6">
                    <div className="border-b border-gray-200">
                        <nav className="flex -mb-px">
                            <button
                                onClick={() => setActiveTab('invoices')}
                                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'invoices'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Invoices ({invoices.length})
                                </div>
                            </button>
                            <button
                                onClick={() => setActiveTab('payments')}
                                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'payments'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                    </svg>
                                    Payments ({payments.length})
                                </div>
                            </button>
                        </nav>
                    </div>
                </div>

                {/* Invoices Tab */}
                {activeTab === 'invoices' && (
                    <div className="space-y-4">
                        {invoices.length === 0 ? (
                            <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Invoices Yet</h3>
                                <p className="text-gray-600">
                                    Your invoices will appear here after you make a purchase.
                                </p>
                            </div>
                        ) : (
                            invoices.map((invoice) => (
                                <div
                                    key={invoice.id}
                                    className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow p-6"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-bold text-gray-900">
                                                        {invoice.invoiceNumber}
                                                    </h3>
                                                    <p className="text-sm text-gray-500">
                                                        {formatDate(invoice.invoiceDate)}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div>
                                                    <p className="text-sm text-gray-500 mb-1">Package</p>
                                                    <p className="font-semibold text-gray-900">{invoice.packageName}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500 mb-1">Duration</p>
                                                    <p className="font-semibold text-gray-900">{invoice.packageDuration} days</p>
                                                </div>
                                            </div>

                                            <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg p-4 mb-4">
                                                <div className="flex justify-between items-center text-sm mb-2">
                                                    <span className="text-gray-600">Base Amount:</span>
                                                    <span className="font-semibold text-gray-900">
                                                        {formatAmount(invoice.baseAmount)}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-center text-sm mb-2">
                                                    <span className="text-gray-600">GST ({invoice.gstRate}%):</span>
                                                    <span className="font-semibold text-gray-900">
                                                        {formatAmount(invoice.gstAmount)}
                                                    </span>
                                                </div>
                                                <div className="border-t border-gray-300 pt-2 mt-2">
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-bold text-gray-900">Total Amount:</span>
                                                        <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                                            {formatAmount(invoice.totalAmount)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                </svg>
                                                <span>Payment ID: {invoice.cashfreePaymentId || invoice.paymentId || invoice.razorpayPaymentId}</span>
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-2 ml-4">
                                            <button
                                                onClick={() => viewInvoice(invoice)}
                                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 text-sm font-medium"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                                View
                                            </button>
                                            <button
                                                onClick={() => downloadInvoice(invoice)}
                                                className="px-4 py-2 bg-white border-2 border-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-50 transition-colors flex items-center gap-2 text-sm font-medium"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                                Download
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Payments Tab */}
                {activeTab === 'payments' && (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        {payments.length === 0 ? (
                            <div className="p-12 text-center">
                                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Payments Yet</h3>
                                <p className="text-gray-600">
                                    Your payment history will appear here after you make a purchase.
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Package
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Amount
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                GST
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Total
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Payment ID
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {payments.map((payment) => (
                                            <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {formatDate(payment.createdAt)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {payment.packageName}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {formatAmount(payment.amount)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {formatAmount(payment.gstAmount || 0)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                                    {formatAmount(payment.totalAmount)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getStatusBadge(payment.status)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                                    {payment.razorpayPaymentId || 'N/A'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                    <div className="bg-white rounded-2xl shadow-sm p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Total Payments</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {payments.filter(p => p.status === 'success').length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Total Spent</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {formatAmount(
                                        payments
                                            .filter(p => p.status === 'success')
                                            .reduce((sum, p) => sum + (p.totalAmount || 0), 0)
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Total Invoices</p>
                                <p className="text-2xl font-bold text-gray-900">{invoices.length}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BillingHistory;
